import dayjs from 'dayjs';
import type Highcharts from 'highcharts';
import { isInteger } from 'lodash';

import { useGetLineChartDefaultOptions } from '../hooks/useGetLineChartDefaultOptions';
import { getReferenceAwareYExtremes } from './referenceLineAxis';

export type LineChartSeries = {
  name: string;
  data: { x: Date | string; y: number; [key: string]: unknown }[];
};

export type LineChartOptionsProps = {
  xTitle: string;
  yTitle: string;
  /** Series colours, cycled by series index. */
  colours: string[];
  /** Category mode: a single unnamed series plotted against formatted date categories. */
  values?: { x: Date; y: number }[];
  /** Datetime mode: named series on a time x-axis. */
  series?: LineChartSeries[];
  tooltipYValueFormatter?: (
    y: number | undefined,
    seriesIndex: number,
    custom?: Record<string, unknown>
  ) => string;
  pointColorFormatter?: (
    y: number | undefined,
    seriesIndex: number,
    custom?: Record<string, unknown>
  ) => string | undefined;
  /**
   * Flat horizontal reference lines (e.g. indicator tolerances/appetites) drawn
   * as Highcharts `yAxis.plotLines`. When present, the y-axis is extended so the
   * lines stay in view.
   */
  referenceLines?: { value: number; color: string }[];
  /**
   * y-axis tick label formatter. Defaults to rendering integer ticks only
   * (dashboard widget behaviour).
   */
  yLabelFormatter?: (value: number) => string;
  /** Allow decimal y ticks (needed when values are fractional). Default false. */
  hasYDecimals?: boolean;
  /** Overrides the legend default (datetime mode shows it when any series exist). */
  isLegendShown?: boolean;
  /**
   * Fixed x-axis window (datetime mode only). The axis spans the whole window
   * regardless of where the data points fall, leaving visible gaps where
   * results are missing (CUS-144).
   */
  xDomain?: [Date, Date];
  /** Fixed chart height in px; defaults to filling the container. */
  height?: number;
};

/**
 * Single source of truth for the Highcharts line-chart options shared by the
 * dashboard `LineChartWidget` and the indicator results-tab `ResultsChart`
 * (CUS-148). Consumers wrap the returned options in their own renderer
 * (`HighchartsWidget` for dashboards, `RSHighcharts` elsewhere).
 */
export const useLineChartOptions = ({
  xTitle,
  yTitle,
  colours,
  values,
  series,
  tooltipYValueFormatter,
  pointColorFormatter,
  referenceLines,
  yLabelFormatter,
  hasYDecimals,
  isLegendShown,
  xDomain,
  height,
}: LineChartOptionsProps): Highcharts.Options => {
  const defaultOptions = useGetLineChartDefaultOptions({});

  const dataYValues = values
    ? values.map((v) => v.y)
    : (series ?? []).flatMap((s) => s.data.map((d) => d.y));

  const yExtremes = referenceLines?.length
    ? getReferenceAwareYExtremes(
        dataYValues,
        referenceLines.map((line) => line.value)
      )
    : undefined;

  const yAxis: Highcharts.YAxisOptions = {
    allowDecimals: hasYDecimals ?? false,
    title: {
      text: yTitle,
    },
    labels: {
      formatter: function formatter() {
        if (yLabelFormatter) {
          return typeof this.value === 'number'
            ? yLabelFormatter(this.value)
            : this.value;
        }

        return hasYDecimals || isInteger(this.value)
          ? this.value.toString()
          : '';
      },
    },
    ...(yExtremes ? { min: yExtremes.min, max: yExtremes.max } : {}),
    plotLines: referenceLines?.map((line) => ({
      value: line.value,
      color: line.color,
      width: 2,
      dashStyle: 'Dash',
    })),
  };

  return {
    ...defaultOptions,
    // The accessibility module isn't loaded (platform-wide convention — see
    // HighchartsWidget); disabling it explicitly suppresses the console warning.
    accessibility: {
      enabled: false,
    },
    chart: {
      ...defaultOptions.chart,
      ...(height == null ? {} : { height }),
      events: pointColorFormatter
        ? {
            redraw: function redraw() {
              this.series.forEach((s, index) => {
                s.points.forEach((point) => {
                  const color = pointColorFormatter(
                    point.y,
                    index,
                    point.options?.custom
                  );
                  if (color) {
                    point.update(
                      {
                        color,
                      },
                      false
                    );
                  }
                });
              });
            },
          }
        : {},
    },
    legend: {
      enabled: isLegendShown ?? (series ? series.length > 0 : false),
    },
    plotOptions: {
      ...defaultOptions.plotOptions,
      series: {
        ...defaultOptions.plotOptions?.series,
        // No point-count cap: indicators can accumulate 1000+ results, which
        // would silently break Highcharts' default turboThreshold (CUS-148).
        turboThreshold: 0,
      },
    },
    tooltip: {
      formatter: function formatter() {
        // Highcharts types `this` as Point but at runtime it's a tooltip
        // context object whose `point` holds the hovered point
        const point =
          (this as unknown as { point?: Highcharts.Point }).point ?? this;
        const yValue = tooltipYValueFormatter
          ? tooltipYValueFormatter(
              this.y,
              this.series.index,
              point.options?.custom
            )
          : this.y;

        // When using categories, this.key holds the formatted category string;
        // for datetime axes, this.key is the timestamp — format it with dayjs
        const xLabel =
          typeof this.key === 'string'
            ? this.key
            : dayjs(this.x).format('D/M/YY');

        return `<b>${xLabel}</b><br/>${this.series.name}: ${yValue}`;
      },
    },
    xAxis: {
      categories: values
        ? values.map((v) => dayjs(v.x).format('D/M/YY'))
        : undefined,
      title: {
        text: xTitle,
      },
      ...(!values
        ? {
            type: 'datetime',
            minTickInterval: 24 * 3600 * 1000, // one day
            labels: {
              formatter: function formatter() {
                return dayjs(this.value).format('D/M/YY');
              },
              rotation: -45,
            },
            ...(xDomain
              ? { min: xDomain[0].getTime(), max: xDomain[1].getTime() }
              : {}),
          }
        : {}),
    },
    yAxis,
    series: values
      ? [
          {
            type: 'line',
            name: yTitle,
            data: values.map((v) => v.y),
            color: colours[0],
          },
        ]
      : series?.map((s, index) => ({
          type: 'line',
          name: s.name,
          data: s.data.map((d) => {
            const { x, y, ...custom } = d;

            return { x: new Date(x).getTime(), y, custom };
          }),
          color: colours[index % colours.length],
        })),
  };
};
